import { useEffect, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Image, Upload } from "lucide-react";
import SEO from "@/components/SEO";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";

const empty = {
    image: "",
    order: 0,
    is_active: true,
};

export default function AdminHeroSlider() {
    const [sliders, setSliders] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editId, setEditId] = useState(null);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);

    const load = async () => {
        try {
            const res = await api.get("/hero-sliders");
            setSliders(res.data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openNew = () => {
        setForm(empty);
        setEditId(null);
        setOpen(true);
    };

    const openEdit = (item) => {
        setForm({
            image: item.image || "",
            order: item.order || 0,
            is_active: item.is_active ?? true,
        });
        setEditId(item.id);
        setOpen(true);
    };

    const setF = (k, v) => {
        setForm((p) => ({ ...p, [k]: v }));
    };

    // ✅ IMAGE UPLOAD HANDLER
    const uploadImage = async (file) => {
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);
        formData.append("kind", "image");

        try {
            setUploading(true);

            const res = await api.post("/admin/upload", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            const url = res.data.url;
            setForm((p) => ({ ...p, image: url }));

            toast.success("Image uploaded");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploading(false);
        }
    };

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);

        try {
            const payload = {
                ...form,
                order: Number(form.order),
            };

            if (editId) {
                await api.put(`/admin/hero-sliders/${editId}`, payload);
                toast.success("Slider updated");
            } else {
                await api.post("/admin/hero-sliders", payload);
                toast.success("Slider created");
            }

            setOpen(false);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const del = async (id) => {
        if (!confirm("Delete this slider?")) return;

        try {
            await api.delete(`/admin/hero-sliders/${id}`);
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const fullUrl = (u) =>
        u?.startsWith("/") ? `${BACKEND_URL}${u}` : u;

    return (
        <div>
            <SEO title="Admin - Hero Slider" />
            {/* HEADER */}
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold">Hero Sliders</h1>
                    <p className="text-xs sm:text-sm text-gray-500">
                        Manage homepage banner sliders
                    </p>
                </div>

                <button
                    onClick={openNew}
                    className="bg-black text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
                >
                    <Plus className="w-4 h-4" />
                    Add Slider
                </button>
            </div>

            {/* TABLE */}
            <div className="bg-white rounded-2xl border overflow-x-auto">
                <table className="w-full text-sm min-w-[500px]">
                    <thead className="bg-gray-50 text-left">
                        <tr>
                            <th className="px-4 py-3">Image</th>
                            <th className="px-4 py-3">Order</th>
                            <th className="px-4 py-3">Status</th>
                            <th className="px-4 py-3 text-right">Action</th>
                        </tr>
                    </thead>

                    <tbody>
                        {sliders.map((s) => (
                            <tr key={s.id} className="border-t">
                                <td className="px-4 py-3">
                                    {s.image ? (
                                        <img
                                            src={fullUrl(s.image)}
                                            className="w-16 sm:w-20 h-10 sm:h-12 object-cover rounded"
                                        />
                                    ) : (
                                        <div className="w-16 sm:w-20 h-10 sm:h-12 bg-gray-100 flex items-center justify-center rounded">
                                            <Image className="w-4 h-4 text-gray-400" />
                                        </div>
                                    )}
                                </td>

                                <td className="px-4 py-3">{s.order}</td>

                                <td className="px-4 py-3 text-xs sm:text-sm">
                                    {s.is_active ? "Active" : "Inactive"}
                                </td>

                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button
                                        onClick={() => openEdit(s)}
                                        className="mr-2"
                                    >
                                        <Edit className="w-4 h-4" />
                                    </button>

                                    <button
                                        onClick={() => del(s.id)}
                                        className="text-red-500"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* MODAL */}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg w-[95vw] sm:w-full z-[100]">
                    <DialogHeader>
                        <DialogTitle>
                            {editId ? "Edit Slider" : "Add Slider"}
                        </DialogTitle>
                    </DialogHeader>

                    <form onSubmit={save} className="space-y-4">

                        {/* IMAGE UPLOAD */}
                        <div>
                            <label className="text-xs font-medium">
                                Upload Image
                            </label>

                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 mt-1">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) =>
                                        uploadImage(e.target.files[0])
                                    }
                                    className="text-xs sm:text-sm w-full sm:w-auto"
                                />

                                {uploading && (
                                    <span className="text-xs text-gray-500">
                                        Uploading...
                                    </span>
                                )}
                            </div>

                            {/* Preview */}
                            {form.image && (
                                <img
                                    src={fullUrl(form.image)}
                                    className="w-full h-32 sm:h-40 object-cover rounded mt-2"
                                />
                            )}
                        </div>

                        {/* MANUAL URL (optional fallback) */}
                        <div>
                            <label className="text-xs font-medium">
                                Image URL (auto filled)
                            </label>

                            <input
                                value={form.image}
                                onChange={(e) => setF("image", e.target.value)}
                                className="w-full border px-3 py-2 rounded-lg text-sm"
                            />
                        </div>

                        {/* ORDER */}
                        <div>
                            <label className="text-xs font-medium">
                                Order
                            </label>
                            <input
                                type="number"
                                value={form.order}
                                onChange={(e) =>
                                    setF("order", e.target.value)
                                }
                                className="w-full border px-3 py-2 rounded-lg text-sm"
                            />
                        </div>

                        {/* ACTIVE */}
                        <label className="flex items-center gap-2 text-sm">
                            <input
                                type="checkbox"
                                checked={form.is_active}
                                onChange={(e) =>
                                    setF("is_active", e.target.checked)
                                }
                            />
                            Active
                        </label>

                        {/* BUTTONS */}
                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="px-4 py-2 border rounded-lg text-sm"
                            >
                                Cancel
                            </button>

                            <button
                                disabled={busy}
                                className="bg-black text-white px-4 py-2 rounded-lg text-sm"
                            >
                                {busy ? "Saving..." : "Save"}
                            </button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}